import {
  Box,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  useLoaderData,
  useParams,
  useNavigate,
  useFetcher,
} from 'react-router';
import {
  DigitalSignature,
  FileLink,
  FileType,
  ImageListItemData,
  Job,
  JobType,
} from 'types';
import {
  capitalize,
  formatViolationsString,
  logError,
} from 'utils';
import { useTranslation } from 'react-i18next';
import PhotoViewer from 'components/layout/PhotoViewer';
import FormFrame from 'components/layout/FormFrame';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import dayjs from 'dayjs';
import { ValidatorForm } from 'react-material-ui-form-validator';
import DetailsHeader from 'components/layout/DetailsHeader';
import JobStatusTimeline from './JobStatusTimeline';
import {
  CancelReason,
  getCancelReasons,
  GetCancelReasonsResponse,
  getJobByID,
} from 'api';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import DigitalSignatureDialog from 'components/layout/DigitalSignatureDialog';
import CancelJobDialog from 'pages/jobs/CancelJobDialog';
import { JobActionHandler, JobsActionResponse } from 'pages/jobs/jobsAction';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { ROUTES, routeWithParams } from 'router';
export interface JobDetailsQueryParams {
  params: {
    tenantID?: number;
    jobID?: number;
  };
}

export interface JobDetailsLoaderData {
  _job: Promise<Job>;
  _cancelReasons: Promise<GetCancelReasonsResponse>;
}

export function jobDetailsLoader({
  params,
}: JobDetailsQueryParams): JobDetailsLoaderData {
  const jobID = params.jobID || 0;
  const tenantID = params.tenantID || 0;

  return {
    _job: getJobByID(tenantID, jobID),
    _cancelReasons:
      getCancelReasons(tenantID) || Promise.resolve({ cancelReasons: [] }),
  } as JobDetailsLoaderData;
}

const JobDetails = (): ReactNode => {
  const { state, dispatch } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const { t } = useTranslation();
  const { _job, _cancelReasons } = useLoaderData() as JobDetailsLoaderData;
  const { jobID, propertyID, groupID } = useParams();
  const [cancelReasons, setCancelReasons] = useState<CancelReason[]>([]);
  const [showDialog, setShowDialog] = useState<
    'photo' | 'signature' | 'cancel' | null
  >(null);
  const [selected, setSelected] = useState<ImageListItemData | null>(null);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [job, setJob] = useState<Job | null>(null);
  const navigate = useNavigate();
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const isMobile = useMediaQuery(useTheme<Theme>().breakpoints.down('lg'));

  // exiting a job returns to the jobs list for the context the job was opened
  // from. navigate(-1) is not safe here: any stray popstate listener or
  // replaced history entry sends the user somewhere unrelated.
  const jobsListRoute = useMemo(() => {
    if (propertyID) {
      return routeWithParams(ROUTES.PROPERTY_JOBS, [
        state.tenant.id,
        propertyID,
      ]);
    }
    if (groupID) {
      return routeWithParams(ROUTES.PROPERTY_GROUP_JOBS, [
        state.tenant.id,
        groupID,
      ]);
    }
    return routeWithParams(ROUTES.JOBS, [state.tenant.id]);
  }, [state.tenant.id, propertyID, groupID]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: `${t('job.label', { ns: 'entities' })} ${t('details')}`,
    });

    const awaitLoader = async () => {
      try {
        const job = await _job;
        setJob(job);
        const cancelReasons = await _cancelReasons;
        if (cancelReasons) {
          setCancelReasons(cancelReasons.cancelReasons || []);
        }
      } catch (e) {
        logError(e as Error);
        navigate(jobsListRoute, { replace: true });
      }
    };
    awaitLoader();
  }, []);

  // cancel job toast handling and job refresh
  useEffect(() => {
    if (!fetcher.data) {
      return;
    }
    const response = fetcher.data as JobsActionResponse;
    if (response.handler !== JobActionHandler.CANCEL) {
      return;
    }
    if (response.error) {
      displayToast({
        message: t('cancel_error', {
          ns: 'toasts',
          name: t('job.label', { ns: 'entities' }),
          error: response.error,
        }),
        type: TOAST_TYPE.ERROR,
      });
      return;
    }
    displayToast({
      message: t('cancel_success', {
        ns: 'toasts',
        name: t('job.label', { ns: 'entities' }),
      }),
      type: TOAST_TYPE.SUCCESS,
    });
    setShowDialog(null);
    const refreshJob = async () => {
      try {
        const refreshed = await getJobByID(state.tenant.id, Number(jobID) || 0);
        setJob(refreshed);
      } catch (e) {
        logError(e as Error);
      }
    };
    refreshJob();
  }, [fetcher.data]);

  const files: ImageListItemData[] = useMemo(() => {
    const imageListItems: ImageListItemData[] = [];

    if (job?.files) {
      job.files.forEach((file: FileLink) => {
        switch (file.fileType) {
          case FileType.Image:
            imageListItems.push({
              img: file.url,
              title: file.title,
              type: FileType.Image,
            });
            break;
          case FileType.Video:
            imageListItems.push({
              img: file.previewUrl || 'fallback.webp',
              title: file.fileKey,
              type: FileType.Video,
            });
            break;
          default:
            imageListItems.push({
              img: 'https://upload.wikimedia.org/wikipedia/commons/6/65/No-Image-Placeholder.svg',
              title: file.title,
              type: FileType.Image,
            });
        }
      });
    }

    if (job?.signatures) {
      job.signatures.forEach((signature: DigitalSignature) => {
        imageListItems.push({
          img: signature.imageURL,
          title: signature.description,
          type: FileType.Image,
          signature: signature,
        });
      });
    }

    return imageListItems;
  }, [job?.files, job?.signatures]);

  const handleShowImage = (image: ImageListItemData) => {
    if (image.signature) {
      setSelected(image);
      setShowDialog('signature');
    } else if (image.type === FileType.Image) {
      setSelected(image);
      setShowDialog('photo');
    }
  };

  const handleCloseImage = () => {
    setSelected(null);
  };

  const handleCancel = () => {
    setIsNavigating(true);
    navigate(jobsListRoute);
  };

  const handleCloseCancelDialog = () => {
    setShowDialog(null);
  };

  return (
    <ValidatorForm onSubmit={() => ({})}>
      {showDialog === 'photo' && selected && (
        <PhotoViewer
          open={!!selected}
          close={handleCloseImage}
          image={selected}
        />
      )}
      {showDialog === 'signature' && selected?.signature && (
        <DigitalSignatureDialog
          open={!!selected}
          close={handleCloseImage}
          signature={selected.signature}
        />
      )}
      {showDialog === 'cancel' && job && cancelReasons.length > 0 && (
        <CancelJobDialog
          isOpen={showDialog === 'cancel'}
          job={job}
          handleClose={handleCloseCancelDialog}
          cancelReasons={cancelReasons}
          fetcherKey={fetcherKey}
        />
      )}
      <FormHeaderBar />
      <FormFrame loading={false}>
        <DetailsHeader
          title={`${t('job.label', { ns: 'entities' })} #${jobID || ''}`}
          onCancel={handleCancel}
          disableCancel={isNavigating}
          hideSubmit
        />
        <ScrollableFormContainer maxHeight={`calc(100dvh - 326px)`}>
          <Stack
            direction={{ xs: 'column-reverse', md: 'row' }}
            gap={4}
            justifyContent='space-between'
          >
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <SettingsSection title={t('steps.general', { ns: 'jobs' })}>
                <Grid container width={'100%'} spacing={1.5}>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      label={`${t('job.type', { ns: 'entities' })}`}
                      name={'jobType'}
                      value={capitalize(job?.type || '')}
                      readonly
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      name={'propertyName'}
                      label={t('job.property.label', { ns: 'entities' })}
                      value={job?.propertyName || ''}
                      readonly
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 3,
                    }}
                  >
                    <AuturaPhoneField
                      name={'contactPhone'}
                      label={t('job.contactPhone', { ns: 'entities' })}
                      phoneNumber={job?.contactPhone || ''}
                      readonly
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      name={'requestTime'}
                      label={t('job.requestTime', { ns: 'entities' })}
                      value={dayjs(job?.requestTime).format(
                        'MM/DD/YYYY hh:mm A',
                      )}
                      readonly
                    />
                  </Grid>
                  <Grid size={12}>
                    <AuturaTextField
                      name={'violations'}
                      label={t('violation.label_plural', { ns: 'entities' })}
                      value={
                        job?.data?.violations
                          ? formatViolationsString(job.data.violations) || ''
                          : ''
                      }
                      readonly
                    />
                  </Grid>
                </Grid>
              </SettingsSection>
              <SettingsSection title={t('steps.location', { ns: 'jobs' })}>
                <Grid container width={'100%'} spacing={1.5}>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      label={t('job.location.street', { ns: 'entities' })}
                      name='location.street'
                      value={job?.location?.street || ''}
                      readonly
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      label={t('job.location.city', { ns: 'entities' })}
                      name='location.city'
                      value={job?.location?.city || ''}
                      readonly
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      label={t('job.location.stateCode', { ns: 'entities' })}
                      name='location.stateCode'
                      value={job?.location?.stateCode || ''}
                      readonly
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      lg: 3,
                    }}
                  >
                    <AuturaTextField
                      label={t('job.location.postalCode', { ns: 'entities' })}
                      name='location.postalCode'
                      value={job?.location?.postalCode || ''}
                      readonly
                    />
                  </Grid>
                  <Grid size={12}>
                    <AuturaTextField
                      label={t('job.location.landmark', { ns: 'entities' })}
                      name='location.landmark'
                      value={job?.location?.landmark || ''}
                      readonly
                    />
                  </Grid>
                </Grid>
              </SettingsSection>
              {[JobType.BOOT, JobType.TAG, JobType.TOW].includes(
                job?.type || JobType.NONE,
              ) && (
                <SettingsSection title={t('steps.vehicle', { ns: 'jobs' })}>
                  <Grid container width={'100%'} spacing={1.5}>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.plate.number', {
                          ns: 'entities',
                        })}
                        name='vehicle.plateNumber'
                        value={job?.plate?.number || ''}
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.plate.state', { ns: 'entities' })}
                        name='vehicle.plate.state'
                        value={job?.plate?.state || ''}
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.plate.exp', { ns: 'entities' })}
                        name='vehicle.plate.expirationDate'
                        value={
                          job?.plate?.exp
                            ? dayjs(new Date(job.plate.exp)).format('MM/YY')
                            : ''
                        }
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.vin', { ns: 'entities' })}
                        name='vehicle.vin'
                        value={job?.vehicle?.vin || ''}
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.year', { ns: 'entities' })}
                        name='vehicle.year'
                        value={job?.vehicle?.year || ''}
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.color', { ns: 'entities' })}
                        name='vehicle.color'
                        value={capitalize(job?.vehicle?.color || '')}
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.make', { ns: 'entities' })}
                        name='vehicle.make'
                        value={job?.vehicle?.make || ''}
                        readonly
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        lg: 3,
                      }}
                    >
                      <AuturaTextField
                        label={t('job.vehicle.model', { ns: 'entities' })}
                        name='vehicle.model'
                        value={job?.vehicle?.model || ''}
                        readonly
                      />
                    </Grid>
                  </Grid>
                </SettingsSection>
              )}
              <SettingsSection
                title={t('steps.notes_and_files', { ns: 'jobs' })}
                divider={false}
              >
                <Grid container width={'100%'} spacing={1.5}>
                  <Grid size={12}>
                    <AuturaTextField
                      name='notes'
                      label={t('job.data.notes', { ns: 'entities' })}
                      value={job?.data?.notes || ''}
                      readonly
                    />
                  </Grid>
                  <Grid size={12}>
                    <ImageList cols={isMobile ? 1 : 4} rowHeight={100} gap={20}>
                      {files.map((item) => (
                        <ImageListItem key={item.img}>
                          <img
                            style={{ height: 100, objectFit: 'contain' }}
                            src={item.img}
                            alt={item.title}
                            loading='lazy'
                            onClick={() => handleShowImage(item)}
                          />
                          <ImageListItemBar title={item.title} />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Grid>
                  <Grid size={12}>
                    {files.map((file) => (
                      <div key={file.title}>{file.title}</div>
                    ))}
                  </Grid>
                </Grid>
              </SettingsSection>
            </Box>
            <Box sx={{ flexShrink: 0, width: '400px' }}>
              <JobStatusTimeline job={job} cancelReasons={cancelReasons} showCancelJobDialog={() => setShowDialog('cancel')} />
            </Box>
          </Stack>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default JobDetails;
